/**
 * Pure timing-issue analyzer for an itinerary day.
 *
 * Given the ordered stops in a day (each carrying `arrivalTime`, optional
 * `durationMinutes`, and the server-computed `travelToHere` into THIS stop),
 * surface the timing problems an author would otherwise have to spot by hand:
 *  - this stop ends AFTER the next one starts (overlap)
 *  - this stop ends with not enough time to drive to the next stop (tight)
 *  - the next stop's start time is earlier than this stop's start (out of order)
 *  - the stop is outside the venue's open hours for the day's weekday
 *
 * Pure: no React, no network, no framework dependencies. Shared by web editor
 * + read-only view and mobile viewer.
 */
import { parseVenueTime } from '../venue/venueHours';
const TIGHT_BUFFER_MINUTES = 5;
const parseHHMM = (hhmm) => {
    if (!hhmm)
        return null;
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
    if (!m)
        return null;
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h < 0 || h > 23 || min < 0 || min > 59)
        return null;
    return h * 60 + min;
};
const formatHHMM = (totalMin) => {
    const wrapped = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};
const formatMinutes = (mins) => {
    if (mins < 60)
        return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};
/**
 * Compute warnings for each stop in `stops`. Returns a map keyed by stop _id
 * with the (possibly empty) warning list — callers can render directly.
 */
export const computeStopWarnings = (stops) => {
    const out = {};
    for (const s of stops)
        out[s._id] = [];
    for (let i = 0; i < stops.length; i++) {
        const cur = stops[i];
        const next = stops[i + 1];
        if (!next)
            continue;
        const curStart = parseHHMM(cur.arrivalTime);
        const nextStart = parseHHMM(next.arrivalTime);
        const curEnd = curStart !== null && cur.durationMinutes != null
            ? curStart + cur.durationMinutes
            : null;
        // OUT_OF_ORDER: next stop starts earlier than this one. Independent of
        // duration / travel — purely a stop-order problem.
        if (curStart !== null && nextStart !== null && nextStart < curStart) {
            out[next._id].push({
                severity: 'warning',
                message: `Starts at ${formatHHMM(nextStart)} — earlier than the previous stop at ${formatHHMM(curStart)}.`,
            });
            continue; // don't double up overlap/tight on top of an order issue
        }
        if (curEnd === null || nextStart === null)
            continue;
        // OVERLAP: this stop ends after the next one starts.
        if (curEnd > nextStart) {
            out[next._id].push({
                severity: 'warning',
                message: `Previous stop ends at ${formatHHMM(curEnd)} — after this stop's start at ${formatHHMM(nextStart)}.`,
            });
            continue;
        }
        // TIGHT_TRAVEL: drive time eats most/all of the gap.
        const gap = nextStart - curEnd;
        const drive = next.travelToHere
            ? Math.round(next.travelToHere.durationSeconds / 60)
            : null;
        if (drive !== null && gap < drive + TIGHT_BUFFER_MINUTES) {
            const shortBy = drive + TIGHT_BUFFER_MINUTES - gap;
            out[next._id].push({
                severity: 'warning',
                message: gap < drive
                    ? `Only ${formatMinutes(Math.max(0, gap))} between stops but ${formatMinutes(drive)} drive — short by ${formatMinutes(shortBy)}.`
                    : `Only ${formatMinutes(gap)} between stops for a ${formatMinutes(drive)} drive — leaves ${formatMinutes(gap - drive)} margin.`,
            });
        }
    }
    return out;
};
const WEEKDAY_KEYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];
const parseISODate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (mo < 1 || mo > 12 || d < 1 || d > 31)
        return null;
    return new Date(y, mo - 1, d);
};
/**
 * Per-stop venue-hours warning. Returns null when:
 *  - day has no date (caller is expected to surface the "no date set" callout)
 *  - venue has no hours data
 *  - stop has no `arrivalTime`
 *  - times all fit inside open hours
 *
 * Surfaces a warning when the venue is closed on the day's weekday, or when
 * the stop's planned window pokes outside open/close.
 */
export const computeVenueHoursWarning = (args) => {
    const { arrivalTime, durationMinutes, dayDate, operatingHours } = args;
    if (!arrivalTime || !dayDate || !operatingHours)
        return null;
    const date = parseISODate(dayDate);
    if (!date)
        return null;
    const weekday = WEEKDAY_KEYS[date.getDay()];
    const hours = operatingHours[weekday];
    if (!hours)
        return null;
    if (hours.closed) {
        return {
            severity: 'warning',
            message: `Venue is closed on ${date.toLocaleDateString(undefined, {
                weekday: 'long',
            })}.`,
        };
    }
    const start = parseHHMM(arrivalTime);
    if (start === null)
        return null;
    const end = durationMinutes != null && Number.isFinite(durationMinutes)
        ? start + durationMinutes
        : start;
    const open = parseVenueTime(hours.open);
    const close = parseVenueTime(hours.close);
    if (open === null || close === null)
        return null;
    if (start < open) {
        return {
            severity: 'warning',
            message: `Starts at ${formatHHMM(start)} but venue opens at ${formatHHMM(open)}.`,
        };
    }
    if (end > close) {
        return {
            severity: 'warning',
            message: `Ends at ${formatHHMM(end)} but venue closes at ${formatHHMM(close)}.`,
        };
    }
    return null;
};
